import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Dialog from '../../../../../../components/base/dialog';
import Button from '../../../../../../components/base/button/button';
import Form, {MuiRFF, Yup} from '../../../../../../components/form';
import ValidateMessages from '../../../../../../../core/models/static/texts/vallidate-messages';
import {mockConflictRules} from '../../mock-data';

const formNames = {
    resolution: 'resolution',
};

const ConflictResolveDialog = ({open, rule, onClose, onResolved}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.loyalty.rules.conflict'});
    const [step, setStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);

    /**
     * Resets wizard step when the dialog opens.
     */
    useEffect(() => {
        if (open) {
            setStep(1);
        }
    }, [open]);

    const schema = useMemo(() => Yup.object().shape({
        [formNames.resolution]: Yup.string()
            .oneOf(['keepThis', 'deactivateOther', 'cancel'])
            .required(ValidateMessages.required),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        [formNames.resolution]: 'keepThis',
    }), [rule?.id, open]);

    /**
     * Applies the selected conflict resolution via mock API.
     */
    const handleSubmit = useCallback(async (values) => {
        const resolution = values[formNames.resolution];
        if (resolution === 'cancel') {
            onClose?.();
            return;
        }
        setSubmitting(true);
        // TODO: Replace with real API when endpoint is available
        const response = await Promise.resolve({resultFlag: true});
        setSubmitting(false);
        if (response?.resultFlag && rule) {
            onResolved?.({...rule, active: resolution !== 'deactivateOther'});
        }
    }, [onClose, onResolved, rule]);

    if (!rule) {
        return null;
    }

    return (
        <Dialog
            open={open}
            setOpen={(nextOpen) => {
                if (!nextOpen) {
                    onClose?.();
                }
            }}
            maxWidth="sm"
        >
            <DialogTitle>{t('title')}</DialogTitle>
            <DialogContent dividers>
                {step === 1 && (
                    <Stack spacing={2} sx={{pt: 1}}>
                        <Typography variant="body2">{t('step1Description', {rule: rule.name})}</Typography>
                        {mockConflictRules.map((conflict) => (
                            <Stack key={conflict.id} sx={{p: 2, bgcolor: 'action.hover', borderRadius: 1}}>
                                <Typography variant="subtitle2">{conflict.name}</Typography>
                                <Typography variant="body2" color="text.secondary">{conflict.reason}</Typography>
                            </Stack>
                        ))}
                    </Stack>
                )}
                {step === 2 && (
                    <Form
                        onSubmit={handleSubmit}
                        validate={validate}
                        initialValues={initialValues}
                        formProps={{id: 'biz-point-conflict-resolve-form'}}
                    >
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
                            <Typography variant="body2">{t('step2Description')}</Typography>
                            <MuiRFF.Radios
                                name={formNames.resolution}
                                required={required[formNames.resolution]}
                                data={[
                                    {label: t('keepThisRule'), value: 'keepThis'},
                                    {label: t('deactivateOther'), value: 'deactivateOther'},
                                    {label: t('cancelActivation'), value: 'cancel'},
                                ]}
                            />
                        </Box>
                    </Form>
                )}
            </DialogContent>
            <Divider/>
            <DialogActions sx={{p: 0, m: 0, justifyContent: 'space-between'}}>
                <Button
                    variant="dialog"
                    color="primary"
                    borderRadius="0 8px 0 8px"
                    onClick={onClose}
                    disabled={submitting}
                >
                    {t('cancel')}
                </Button>
                {step === 1 && (
                    <Button
                        variant="dialog"
                        color="primary"
                        borderRadius="8px 0 8px 0"
                        onClick={() => setStep(2)}
                    >
                        {t('next')}
                    </Button>
                )}
                {step === 2 && (
                    <Button
                        variant="dialog"
                        color="primary"
                        borderRadius="8px 0 8px 0"
                        type="submit"
                        form="biz-point-conflict-resolve-form"
                        disabled={submitting}
                    >
                        {t('resolve')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ConflictResolveDialog;
